import React from "react";
import PersonIcon from "@mui/icons-material/Person";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import ManagerInputModal from "./ManagerInputModal";

function ManagerInputRecordTable({ record, onBack, employeeAvatarColorByUserId, homeCompany }) {
  // Local copies so an inline edit updates the row without a full refetch.
  const [submissions, setSubmissions] = React.useState(record.submissions || []);
  const [incomplete, setIncomplete] = React.useState(record.incomplete_assignments || []);
  const [editTarget, setEditTarget] = React.useState(null);

  React.useEffect(() => {
    setSubmissions(record.submissions || []);
    setIncomplete(record.incomplete_assignments || []);
  }, [record]);

  // A manager can record/edit input here only when the detail payload carries the
  // block + section to post against (and we know the company).
  const canAct = Boolean(
    homeCompany && record.manager_input_block_uuid && record.section_uuid
  );

  const handleSaved = (target, newValue, completion) => {
    const stmId = target.store_team_member_id;
    const updated = {
      ...target,
      input_value: newValue,
      submitted_by:
        completion?.manager_name ||
        completion?.submitted_by ||
        target.submitted_by ||
        "You",
      // Optimistic ISO timestamp (existing rows are ISO; the serializer returns
      // epoch-ms, so don't reuse it). A refetch replaces this with the server value.
      date_completed: new Date().toISOString(),
    };
    setSubmissions((prev) => {
      const exists = prev.some((s) => s.store_team_member_id === stmId);
      return exists
        ? prev.map((s) => (s.store_team_member_id === stmId ? { ...s, ...updated } : s))
        : [updated, ...prev];
    });
    setIncomplete((prev) => prev.filter((a) => a.store_team_member_id !== stmId));
  };

  const formatDateReadable = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasSubmissionData = submissions && submissions.length > 0;
  const hasIncompleteData = incomplete && incomplete.length > 0;
  const hasEmployeeData = hasSubmissionData || hasIncompleteData;

  const exportManagerInputCSV = () => {
    const rows = [];
    (submissions || []).forEach((submission) => {
      rows.push({
        "Employee": submission.employee_name,
        "Input Value": submission.input_value,
        "Submitted By": submission.submitted_by || "",
        "Date Completed": submission.date_completed
          ? formatDateReadable(submission.date_completed)
          : "",
      });
    });
    (incomplete || []).forEach((assignment) => {
      rows.push({
        "Employee": assignment.employee_name,
        "Input Value": "Pending Manager Input",
        "Submitted By": "",
        "Date Completed": "",
      });
    });

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    downloadCSV(csv, `manager_input_${record.name.replace(/\s+/g, "_")}.csv`);
  };

  return (
    <div className="px-6 py-8">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
        >
          <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
          Back to Records
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {record.name}
            </h2>
            <p className="text-sm text-slate-600">Manager input records</p>
          </div>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            onClick={exportManagerInputCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Manager Input Details Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Description
            </div>
            <div className="text-sm text-slate-900">
              {record.description || "No description provided"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Required
            </div>
            <div className="text-sm font-medium text-slate-900">
              {record.required ? "Yes" : "No"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Total Submissions
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {submissions.length} / {record.assigned_count}
            </div>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {hasEmployeeData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Input Value
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Submitted By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date Completed
                  </th>
                  {canAct && <th className="px-6 py-4" />}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {submissions.map((submission, index) => (
                  <tr
                    key={`completed-${index}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={submission.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                submission.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {submission.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                          <CheckCircleIcon
                            className="text-emerald-600"
                            style={{ fontSize: "18px" }}
                          />
                          <span className="text-base font-semibold text-slate-900">
                            {submission.input_value}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <PersonIcon
                          className="text-slate-400"
                          style={{ fontSize: "16px" }}
                        />
                        <span className="font-medium">{submission.submitted_by}</span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CalendarTodayIcon style={{ fontSize: "16px" }} />
                        <span>{formatDateReadable(submission.date_completed)}</span>
                      </div>
                    </td>
                    {canAct && (
                      <td className="align-middle px-6 py-3 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => setEditTarget(submission)}
                          className="text-xs font-medium text-violet-700 hover:text-violet-800"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {incomplete.map((assignment, index) => (
                  <tr
                    key={`pending-${index}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={assignment.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                assignment.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {assignment.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                          Pending Manager Input
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                    {canAct && (
                      <td className="align-middle px-6 py-3 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => setEditTarget(assignment)}
                          className="text-xs font-medium text-violet-700 hover:text-violet-800"
                        >
                          Enter input
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <InfoOutlinedIcon
              className="text-slate-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-slate-600 font-medium mb-2">
              Employee-level submission data not available
            </p>
            <p className="text-slate-400 text-sm">
              Summary: {record.total_submissions} of {record.assigned_count} employees
              have manager input recorded.
            </p>
          </div>
        )}
      </div>

      <ManagerInputModal
        open={!!editTarget}
        record={record}
        submission={editTarget}
        homeCompany={homeCompany}
        onClose={() => setEditTarget(null)}
        onSaved={handleSaved}
      />
    </div>
  );
}

export default ManagerInputRecordTable;
